import React from "react";
import { Icon, notify } from "../registry";
import type { Photo, Block, Faq, Card, Step } from "./types";

/* ============================================================
   Website builder — small, generic field editors.
   Every editor is controlled: value + onChange(next). Lists copy on write.
   WsCtx carries whether we're talking to the service (uploads need it).
   ============================================================ */

export const WsCtx = React.createContext<{ live: boolean }>({ live: false });
export const OFFLINE_MSG = "Connect to the Fence Works service to do this — the sample site doesn't save.";

export function Field({ label, hint, right, children, className }: any) {
  return (
    <div className={"ws-field" + (className ? " " + className : "")}>
      {(label || right) && <div className="ws-field-top">{label && <label className="field-label">{label}</label>}{right}</div>}
      {children}
      {hint && <div className="ws-hint">{hint}</div>}
    </div>
  );
}

function Count({ n, max }: { n: number; max: number }) {
  return <span className={"ws-count tnum" + (n > max ? " over" : "")}>{n}/{max}</span>;
}

export function TextField({ label, value, onChange, placeholder, hint, max, type = "text", readOnly, inputMode, style }: any) {
  const v = value == null ? "" : String(value);
  return (
    <Field label={label} hint={hint} right={max ? <Count n={v.length} max={max} /> : null}>
      <input className="input ws-input" type={type} value={v} placeholder={placeholder} readOnly={readOnly} inputMode={inputMode} style={style}
        onChange={(e: any) => onChange && onChange(e.target.value)} />
    </Field>
  );
}

export function NumField({ label, value, onChange, hint, step, min, max }: any) {
  return (
    <Field label={label} hint={hint}>
      <input className="input ws-input tnum" type="number" value={value == null || Number.isNaN(value) ? "" : value} step={step} min={min} max={max}
        onChange={(e: any) => onChange(e.target.value === "" ? 0 : Number(e.target.value))} />
    </Field>
  );
}

export function TextArea({ label, value, onChange, placeholder, hint, max, rows = 3 }: any) {
  const v = value == null ? "" : String(value);
  return (
    <Field label={label} hint={hint} right={max ? <Count n={v.length} max={max} /> : null}>
      <textarea className="textarea ws-input" rows={rows} value={v} placeholder={placeholder} onChange={(e: any) => onChange(e.target.value)} />
    </Field>
  );
}

export function Toggle({ label, value, onChange, hint, disabled }: any) {
  return (
    <div className="ws-toggle">
      <button type="button" className={"sw" + (value ? " on" : "")} role="switch" aria-checked={!!value} aria-label={label} disabled={disabled}
        onClick={() => onChange(!value)}><span className="knob" /></button>
      <div style={{ minWidth: 0 }}>
        <div className="ws-toggle-label" onClick={() => !disabled && onChange(!value)}>{label}</div>
        {hint && <div className="ws-hint" style={{ marginTop: 1 }}>{hint}</div>}
      </div>
    </div>
  );
}

export function SelectField({ label, value, onChange, options, hint }: any) {
  return (
    <Field label={label} hint={hint}>
      <select className="select ws-input" value={value == null ? "" : value} onChange={(e: any) => onChange(e.target.value)}>
        {options.map((o: any) => { const [v, l] = Array.isArray(o) ? o : [o, o]; return <option key={v} value={v}>{l}</option>; })}
      </select>
    </Field>
  );
}

/* a collapsible block inside a tab */
export function Group({ title, sub, right, open, onToggle, defaultOpen = true, children, badge }: any) {
  const [o, setO] = React.useState<boolean>(defaultOpen);
  const isOpen = open != null ? open : o;
  const flip = () => (onToggle ? onToggle(!isOpen) : setO(!isOpen));
  return (
    <section className={"ws-group" + (isOpen ? " open" : "")}>
      <div className="ws-group-head">
        <button type="button" className="ws-group-btn" onClick={flip} aria-expanded={isOpen}>
          <Icon name="chevdown" size={15} style={{ transform: isOpen ? "none" : "rotate(-90deg)", transition: "transform .15s", flex: "0 0 auto" }} />
          <span style={{ minWidth: 0 }}><b>{title}</b>{sub && <span className="faint"> · {sub}</span>}</span>
          {badge}
        </button>
        {right && <div className="ws-group-right">{right}</div>}
      </div>
      {isOpen && <div className="ws-group-body">{children}</div>}
    </section>
  );
}

/* ---- list plumbing: move / remove / add ---- */
const move = (arr: any[], i: number, d: number) => { const j = i + d; if (j < 0 || j >= arr.length) return arr; const c = arr.slice(); [c[i], c[j]] = [c[j], c[i]]; return c; };
function ItemTools({ i, n, onMove, onRemove, label }: any) {
  return (
    <span className="ws-tools">
      <button type="button" className="ws-ib" title="Move up" aria-label={"Move " + label + " up"} disabled={i === 0} onClick={() => onMove(i, -1)}><Icon name="chevdown" size={14} style={{ transform: "rotate(180deg)" }} /></button>
      <button type="button" className="ws-ib" title="Move down" aria-label={"Move " + label + " down"} disabled={i === n - 1} onClick={() => onMove(i, 1)}><Icon name="chevdown" size={14} /></button>
      <button type="button" className="ws-ib danger" title="Remove" aria-label={"Remove " + label} onClick={() => onRemove(i)}><Icon name="x" size={14} /></button>
    </span>
  );
}
function AddBtn({ onClick, label }: any) {
  return <button type="button" className="ws-add" onClick={onClick}><Icon name="plus" size={13} />{label}</button>;
}

export function StringListEditor({ label, value, onChange, placeholder, multiline, addLabel = "Add a line", hint, rows = 2 }: any) {
  const items: string[] = Array.isArray(value) ? value : [];
  const set = (i: number, v: string) => onChange(items.map((x, j) => (j === i ? v : x)));
  return (
    <Field label={label} hint={hint}>
      <div className="ws-list">
        {items.map((x, i) => (
          <div key={i} className="ws-li">
            {multiline
              ? <textarea className="textarea ws-input" rows={rows} value={x} placeholder={placeholder} onChange={(e: any) => set(i, e.target.value)} />
              : <input className="input ws-input" value={x} placeholder={placeholder} onChange={(e: any) => set(i, e.target.value)} />}
            <ItemTools i={i} n={items.length} label="line" onMove={(a: number, d: number) => onChange(move(items, a, d))} onRemove={(a: number) => onChange(items.filter((_, j) => j !== a))} />
          </div>
        ))}
        <AddBtn label={addLabel} onClick={() => onChange([...items, ""])} />
      </div>
    </Field>
  );
}

/* a list of small objects, each edited by a row of fields */
type ObjField = { k: string; label?: string; kind?: "text" | "area" | "num" | "bool"; placeholder?: string; width?: number; rows?: number };
export function ObjListEditor({ label, value, onChange, fields, blank, addLabel = "Add", hint, titleOf, noun = "item" }: { label?: any; value: any[]; onChange: (v: any[]) => void; fields: ObjField[]; blank: (items: any[]) => any; addLabel?: string; hint?: any; titleOf?: (x: any, i: number) => string; noun?: string }) {
  const items: any[] = Array.isArray(value) ? value : [];
  const set = (i: number, k: string, v: any) => onChange(items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const inline = fields.filter((f) => f.kind !== "area");
  const blocks = fields.filter((f) => f.kind === "area");
  return (
    <Field label={label} hint={hint}>
      <div className="ws-list">
        {items.map((x, i) => (
          <div key={i} className="ws-obj">
            <div className="ws-obj-row">
              {titleOf && <span className="ws-obj-n tnum">{titleOf(x, i)}</span>}
              {inline.map((f) => f.kind === "bool" ? (
                <label key={f.k} className="ws-check"><input type="checkbox" checked={!!x[f.k]} onChange={(e: any) => set(i, f.k, e.target.checked)} />{f.label}</label>
              ) : (
                <input key={f.k} className="input ws-input" aria-label={f.label || f.k} placeholder={f.placeholder || f.label} type={f.kind === "num" ? "number" : "text"}
                  style={f.width ? { flex: "0 0 " + f.width + "px", width: f.width } : { flex: 1, minWidth: 0 }}
                  value={x[f.k] == null ? "" : x[f.k]} onChange={(e: any) => set(i, f.k, f.kind === "num" ? Number(e.target.value || 0) : e.target.value)} />
              ))}
              <ItemTools i={i} n={items.length} label={noun} onMove={(a: number, d: number) => onChange(move(items, a, d))} onRemove={(a: number) => onChange(items.filter((_, j) => j !== a))} />
            </div>
            {blocks.map((f) => (
              <textarea key={f.k} className="textarea ws-input" rows={f.rows || 2} aria-label={f.label || f.k} placeholder={f.placeholder || f.label} value={x[f.k] || ""} onChange={(e: any) => set(i, f.k, e.target.value)} />
            ))}
          </div>
        ))}
        <AddBtn label={addLabel} onClick={() => onChange([...items, blank(items)])} />
      </div>
    </Field>
  );
}

export function CardListEditor({ label, value, onChange, hint, addLabel = "Add a card" }: { label?: any; value: Card[]; onChange: (v: Card[]) => void; hint?: any; addLabel?: string }) {
  return <ObjListEditor label={label} hint={hint} value={value} onChange={onChange} noun="card" addLabel={addLabel}
    fields={[{ k: "t", label: "Title" }, { k: "d", label: "Description", kind: "area" }]} blank={() => ({ t: "", d: "" })} />;
}
const nextN = (items: Step[]) => { const last = items[items.length - 1]; const k = last && /^\d+$/.test(last.n) ? Number(last.n) + 1 : items.length + 1; return String(k).padStart(2, "0"); };
export function StepListEditor({ label, value, onChange, hint, addLabel = "Add a step", nLabel = "No." }: { label?: any; value: Step[]; onChange: (v: Step[]) => void; hint?: any; addLabel?: string; nLabel?: string }) {
  return <ObjListEditor label={label} hint={hint} value={value} onChange={onChange} noun="step" addLabel={addLabel}
    fields={[{ k: "n", label: nLabel, width: 78 }, { k: "t", label: "Title" }, { k: "d", label: "Description", kind: "area" }]} blank={(items) => ({ n: nextN(items), t: "", d: "" })} />;
}
export function FaqEditor({ label = "FAQs", value, onChange, hint }: { label?: any; value: Faq[]; onChange: (v: Faq[]) => void; hint?: any }) {
  return <ObjListEditor label={label} hint={hint || "Real questions customers ask you. Google shows good FAQs right in the results."} value={value} onChange={onChange} noun="question" addLabel="Add a question"
    fields={[{ k: "q", label: "Question" }, { k: "a", label: "Answer", kind: "area", rows: 3 }]} blank={() => ({ q: "", a: "" })} />;
}

/* chips: towns, neighborhoods, short tags */
export function ChipsInput({ label, value, onChange, placeholder, hint }: any) {
  const items: string[] = Array.isArray(value) ? value : [];
  const [t, setT] = React.useState("");
  const add = (raw: string) => {
    const vals = String(raw).split(/[,;\n]/).map((x) => x.trim()).filter(Boolean).filter((x) => !items.some((y) => y.toLowerCase() === x.toLowerCase()));
    if (vals.length) onChange([...items, ...vals]);
    setT("");
  };
  return (
    <Field label={label} hint={hint}>
      <div className="ws-chips">
        {items.map((x, i) => (
          <span key={x + i} className="ws-chip">{x}<button type="button" aria-label={"Remove " + x} onClick={() => onChange(items.filter((_, j) => j !== i))}><Icon name="x" size={11} /></button></span>
        ))}
        <input className="ws-chips-input" value={t} placeholder={items.length ? "Add another…" : placeholder}
          onChange={(e: any) => { const v = e.target.value; if (/[,;]$/.test(v)) add(v); else setT(v); }}
          onKeyDown={(e: any) => { if (e.key === "Enter") { e.preventDefault(); add(t); } else if (e.key === "Backspace" && !t && items.length) onChange(items.slice(0, -1)); }}
          onBlur={() => t.trim() && add(t)} />
      </div>
    </Field>
  );
}

/* ---- photos ---- */
const MAX_UPLOAD = 8 * 1024 * 1024;
export function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(String(r.result || "")); r.onerror = () => reject(r.error || new Error("Couldn't read that file")); r.readAsDataURL(file); });
}
/* one upload path for photos and logos: checks, dataURL, POST /sites/me/upload */
export async function uploadImage(file: File, kind: "photo" | "logo", live: boolean): Promise<string | null> {
  if (!file) return null;
  if (!live) { notify(OFFLINE_MSG); return null; }
  if (!/^image\/(png|jpe?g|webp|svg\+xml)$/.test(file.type)) { notify("Use a PNG, JPG, WebP or SVG image.", { kind: "error" }); return null; }
  if (file.size > MAX_UPLOAD) { notify("That image is over 8 MB — export a smaller copy and try again.", { kind: "error" }); return null; }
  const DB: any = (window as any).VFP_DB;
  try {
    const dataUrl = await readAsDataUrl(file);
    const r = await DB.siteUpload(dataUrl, kind);
    if (!r || !r.url) throw new Error("The upload didn't return a link.");
    return r.url;
  } catch (e: any) { notify(e.message || "Upload failed — try again.", { kind: "error" }); return null; }
}

export function PhotoField({ label, value, onChange, hint, allowClear, compact }: { label?: any; value?: Photo | null; onChange: (p: Photo | undefined) => void; hint?: any; allowClear?: boolean; compact?: boolean }) {
  const { live } = React.useContext(WsCtx);
  const ref = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy] = React.useState(false);
  const p: Photo = value || { src: "", alt: "" };
  const pick = async (f: File | undefined) => {
    if (!f) return;
    setBusy(true);
    const url = await uploadImage(f, "photo", live);
    setBusy(false);
    if (ref.current) ref.current.value = "";
    if (url) { onChange({ ...p, src: url, alt: p.alt && !p.example ? p.alt : "", example: false }); notify("Photo uploaded — add a few words about it below."); }
  };
  return (
    <Field label={label} hint={hint}>
      <div className={"ws-photo" + (compact ? " compact" : "")}>
        <div className="ws-thumb">
          {p.src ? <img src={p.src} alt={p.alt || ""} loading="lazy" /> : <Icon name="image" size={22} />}
          {p.src && p.example && <span className="ws-example">Example</span>}
        </div>
        <div className="ws-photo-side">
          <div className="row gap-8" style={{ flexWrap: "wrap" }}>
            <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: "none" }} onChange={(e: any) => pick(e.target.files && e.target.files[0])} />
            <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => (live ? ref.current && ref.current.click() : notify(OFFLINE_MSG))}>
              <Icon name="image" size={13} />{busy ? "Uploading…" : p.src ? "Replace" : "Upload"}</button>
            {allowClear && p.src && <button type="button" className="rv-link" onClick={() => onChange(undefined)}>Remove</button>}
          </div>
          {p.src && p.example && <div className="ws-nudge"><Icon name="bolt" size={12} />Replace with your own job photo — a real fence you built sells better than any stock shot.</div>}
          {p.src && <input className="input ws-input" value={p.alt || ""} placeholder="Describe it: 6 ft white vinyl privacy fence, De Pere backyard" aria-label="Photo description (alt text)"
            onChange={(e: any) => onChange({ ...p, alt: e.target.value })} />}
        </div>
      </div>
    </Field>
  );
}

export function PhotoListEditor({ label, value, onChange, hint }: { label?: any; value: Photo[]; onChange: (v: Photo[]) => void; hint?: any }) {
  const { live } = React.useContext(WsCtx);
  const ref = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy] = React.useState(false);
  const items: Photo[] = Array.isArray(value) ? value : [];
  const add = async (files: FileList | null) => {
    if (!files || !files.length) return;
    setBusy(true);
    const got: Photo[] = [];
    for (const f of Array.from(files).slice(0, 12)) { const url = await uploadImage(f, "photo", live); if (url) got.push({ src: url, alt: "", example: false }); }
    setBusy(false);
    if (ref.current) ref.current.value = "";
    if (got.length) onChange([...items, ...got]);
  };
  const examples = items.filter((x) => x.example).length;
  return (
    <Field label={label} hint={hint}>
      <div className="ws-gallery">
        {items.map((x, i) => (
          <div key={i} className="ws-gthumb">
            <img src={x.src} alt={x.alt || ""} loading="lazy" />
            {x.example && <span className="ws-example">Example</span>}
            <button type="button" className="ws-gx" aria-label="Remove photo" onClick={() => onChange(items.filter((_, j) => j !== i))}><Icon name="x" size={12} /></button>
            <input className="ws-galt" value={x.alt || ""} placeholder="Describe it" aria-label="Photo description" onChange={(e: any) => onChange(items.map((y, j) => (j === i ? { ...y, alt: e.target.value } : y)))} />
          </div>
        ))}
        <input ref={ref} type="file" multiple accept="image/png,image/jpeg,image/webp" style={{ display: "none" }} onChange={(e: any) => add(e.target.files)} />
        <button type="button" className="ws-gadd" disabled={busy} onClick={() => (live ? ref.current && ref.current.click() : notify(OFFLINE_MSG))}><Icon name="plus" size={16} /><span>{busy ? "Uploading…" : "Add photos"}</span></button>
      </div>
      {examples > 0 && <div className="ws-nudge" style={{ marginTop: 6 }}><Icon name="bolt" size={12} />{examples} example photo{examples === 1 ? "" : "s"} — swap in your own jobs.</div>}
    </Field>
  );
}

/* ---- blog body blocks: h / h3 / p / list / tip ---- */
const BLOCK_LABEL: Record<string, string> = { h: "Heading", h3: "Subheading", p: "Paragraph", list: "Bullet list", tip: "Tip box" };
export function BlockEditor({ value, onChange }: { value: Block[]; onChange: (v: Block[]) => void }) {
  const items: Block[] = Array.isArray(value) ? value : [];
  const set = (i: number, b: Block) => onChange(items.map((x, j) => (j === i ? b : x)));
  const add = (t: Block["t"]) => onChange([...items, t === "list" ? { t: "list", items: [""] } : ({ t, x: "" } as Block)]);
  return (
    <div className="ws-list">
      {items.map((b, i) => (
        <div key={i} className={"ws-block ws-block-" + b.t}>
          <div className="ws-obj-row">
            <select className="select ws-input ws-block-kind" value={b.t} aria-label="Block type" onChange={(e: any) => {
              const t = e.target.value as Block["t"];
              const text = b.t === "list" ? (b as any).items.join("\n") : (b as any).x;
              set(i, t === "list" ? { t: "list", items: String(text || "").split("\n") } : ({ t, x: text || "" } as Block));
            }}>
              {Object.keys(BLOCK_LABEL).map((k) => <option key={k} value={k}>{BLOCK_LABEL[k]}</option>)}
            </select>
            <span style={{ flex: 1 }} />
            <ItemTools i={i} n={items.length} label="block" onMove={(a: number, d: number) => onChange(move(items, a, d))} onRemove={(a: number) => onChange(items.filter((_, j) => j !== a))} />
          </div>
          {b.t === "list"
            ? <textarea className="textarea ws-input" rows={Math.max(2, b.items.length)} value={b.items.join("\n")} placeholder="One bullet per line" onChange={(e: any) => set(i, { t: "list", items: e.target.value.split("\n") })} />
            : b.t === "p" || b.t === "tip"
              ? <textarea className="textarea ws-input" rows={b.t === "p" ? 4 : 2} value={b.x} placeholder={b.t === "tip" ? "A quick tip, shown in a box" : "Paragraph — links: [text](/path)"} onChange={(e: any) => set(i, { t: b.t, x: e.target.value })} />
              : <input className={"input ws-input" + (b.t === "h" ? " ws-h" : "")} value={b.x} placeholder={BLOCK_LABEL[b.t]} onChange={(e: any) => set(i, { t: b.t, x: e.target.value } as Block)} />}
        </div>
      ))}
      <div className="row gap-8" style={{ flexWrap: "wrap" }}>
        {(["h", "p", "list", "tip", "h3"] as Block["t"][]).map((t) => <AddBtn key={t} label={BLOCK_LABEL[t]} onClick={() => add(t)} />)}
      </div>
    </div>
  );
}

/* the AI button + its credit note */
export function AiButton({ onClick, busy, label = "Rewrite with AI", small = true }: any) {
  return (
    <button type="button" className={"btn btn-outline" + (small ? " btn-sm" : "")} disabled={!!busy} onClick={onClick} title="Uses 1 AI credit. Nothing changes until you press Apply.">
      <Icon name="bolt" size={13} />{busy ? "Writing…" : label}
    </button>
  );
}

export const copyText = (t: string, what = "Copied") => {
  try { navigator.clipboard.writeText(t); notify(what + "."); } catch (e) { notify("Copy failed — select it and copy by hand.", { kind: "error" }); }
};
